import type { LedgerRules, LedgerRun, StrategySummary } from '../../lib/bankrollLedger';
import { MIN_READABLE_BETS } from '../../lib/forwardRuleSummary';
import {
  centsSign,
  formatPercent,
  formatSignedPercent,
  formatSignedUsd,
  formatUsd,
} from '../../lib/format';
import { BankrollChart } from './BankrollChart';
import styles from './Btc15TrackRecord.module.css';

export interface TrackRecordRow {
  key: string;
  /** e.g. "Baleen There". */
  name: string;
  /** What it actually is, e.g. "live heuristic". */
  descriptor: string;
  /** What leaves a round without a bet for this particular strategy. */
  note: string;
  summary: StrategySummary;
  /** A reference baseline (coin flip, dice), listed apart from the models. */
  baseline?: boolean;
}

/**
 * Where the figures come from: the server's permanent ledger, or only the
 * stored 1,200-round histories, because the ledger is still loading, could
 * not be loaded, or has not had its first update.
 */
export type TrackRecordSource =
  'ledger' | 'window-loading' | 'window-error' | 'window-pending';

const WINDOW_NOTE: Record<Exclude<TrackRecordSource, 'ledger'>, string> = {
  'window-loading':
    'Showing the last 1,200 rounds (about 12 days) while the permanent ledger loads.',
  'window-error':
    'The permanent ledger could not be loaded. Showing the last 1,200 rounds (about 12 days) instead.',
  'window-pending':
    "Showing the last 1,200 rounds (about 12 days) until the permanent ledger's first update.",
};

const dateTime = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const shortDate = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const day = (ms: number) => shortDate.format(ms);

const cents = (x: number) => `${x >= 0 ? '+' : '−'}${Math.abs(x * 100).toFixed(1)}¢`;
const count = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

function pnlClass(value: number): string {
  const sign = centsSign(value);
  return sign > 0 ? styles.positive : sign < 0 ? styles.negative : styles.neutral;
}

/** The third, quietest line of a collapsed row: the money and the run. */
function statusLine(s: StrategySummary): string {
  if (s.calls === 0) return 'No settled calls yet';
  if (s.bets === 0) return `No bets yet, ${count(s.calls, 'call')} settled`;
  return [
    `Net ${formatSignedUsd(s.pnl)}`,
    `Run ${s.runCount}`,
    s.busts === 0 ? 'no busts' : count(s.busts, 'bust'),
    `${formatUsd(s.bankroll)} left`,
  ].join(' · ');
}

/** Enough one-contract bets for the per-contract figure to mean anything. */
function flatReadable(s: StrategySummary): boolean {
  return s.flat.bets >= MIN_READABLE_BETS && s.flat.netPerContract !== null;
}

/** The second line's right side: the likely range, or why there isn't one. */
function rangeLine(s: StrategySummary): string | null {
  if (s.flat.bets === 0 || s.flat.netPerContract === null) return null;
  if (!flatReadable(s)) return `${count(s.flat.bets, 'bet')}, too early`;
  if (s.flat.netLow === null || s.flat.netHigh === null) return null;
  return `likely ${cents(s.flat.netLow)} to ${cents(s.flat.netHigh)}`;
}

/**
 * Best per-contract result first: the one figure sizing and busts can't
 * flatter. Models too new to read follow (most bets first), then any with
 * no settled calls.
 */
function orderTrackRecordRows(rows: readonly TrackRecordRow[]): TrackRecordRow[] {
  const rank = (row: TrackRecordRow) =>
    row.summary.calls === 0 ? 2 : flatReadable(row.summary) ? 0 : 1;
  return [...rows].sort((a, b) => {
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;
    if (rank(a) === 0) {
      return (b.summary.flat.netPerContract ?? 0) - (a.summary.flat.netPerContract ?? 0);
    }
    return b.summary.flat.bets - a.summary.flat.bets;
  });
}

function RunsTable({ current, past }: { current: LedgerRun; past: LedgerRun[] }) {
  return (
    <table className={styles.runs}>
      <caption className="visually-hidden">Bankroll runs, newest first</caption>
      <thead>
        <tr>
          <th scope="col">Run</th>
          <th scope="col">Dates</th>
          <th scope="col">Bets</th>
          <th scope="col">Peak</th>
          <th scope="col">End</th>
        </tr>
      </thead>
      <tbody>
        {[current, ...past].map((run) => (
          <tr key={run.n}>
            <th scope="row">{run.n}</th>
            <td>
              {day(run.startedMs)} – {run.endedMs === null ? 'now' : day(run.endedMs)}
            </td>
            <td>{run.bets.toLocaleString()}</td>
            <td>{formatUsd(run.peak)}</td>
            <td>
              {formatUsd(run.end)}
              {run.endedMs !== null && <span className="visually-hidden"> (bust)</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RowDetails({ row, rules }: { row: TrackRecordRow; rules: LedgerRules }) {
  const s = row.summary;
  if (s.calls === 0) {
    return <p className={styles.fine}>{row.note}</p>;
  }
  const readable = flatReadable(s);
  return (
    <>
      {s.series.length > 1 && (
        <BankrollChart
          series={s.series}
          startingPool={rules.startingPool}
          fromStart={s.series.length === s.bets}
        />
      )}
      <dl className={styles.facts}>
        <div>
          <dt>Paid in</dt>
          <dd>{formatUsd(s.deposited)}</dd>
        </div>
        <div>
          <dt>Left</dt>
          <dd>{formatUsd(s.bankroll)}</dd>
        </div>
        <div>
          <dt>Net after fees</dt>
          <dd className={pnlClass(s.pnl)}>{formatSignedUsd(s.pnl)}</dd>
        </div>
        <div>
          <dt>Bets won</dt>
          <dd>
            {s.wins.toLocaleString()} of {s.bets.toLocaleString()}
          </dd>
        </div>
        <div>
          <dt>Per dollar bet</dt>
          <dd>{s.roi === null ? '—' : formatSignedPercent(s.roi * 100, 1)}</dd>
        </div>
        <div>
          <dt>Calls right</dt>
          <dd>
            {formatPercent(s.hitRate ?? undefined, 0)} of {s.calls.toLocaleString()}
          </dd>
        </div>
      </dl>
      <p className={styles.fine}>
        <strong>One contract a bet:</strong>{' '}
        {readable ? (
          <>
            {cents(s.flat.netPerContract as number)} a contract after fees over{' '}
            {count(s.flat.bets, 'bet')}
            {s.flat.netLow !== null && s.flat.netHigh !== null && (
              <>
                {' '}
                (likely range {cents(s.flat.netLow)} to {cents(s.flat.netHigh)})
              </>
            )}
            . This ignores the bankroll, so sizing and busts can&apos;t flatter or hide
            it.
          </>
        ) : (
          <>{count(s.flat.bets, 'bet')} so far, too early to read.</>
        )}
      </p>
      {s.current && <RunsTable current={s.current} past={s.past} />}
      {s.past.length > 0 && (
        <p className={styles.fine}>
          Every finished run ended in a bust: its bankroll was too small to place the next
          bet at the $1 minimum.
          {s.busts > s.past.length && (
            <> The {count(s.past.length, 'most recent bust')} are listed.</>
          )}
        </p>
      )}
      <p className={styles.fine}>
        {formatUsd(s.staked)} staked, {formatUsd(s.fees)} of it fees. {row.note}
      </p>
    </>
  );
}

function RowList({ rows, rules }: { rows: TrackRecordRow[]; rules: LedgerRules }) {
  return (
    <ul className={styles.rows}>
      {rows.map((row) => {
        const s = row.summary;
        const range = rangeLine(s);
        return (
          <li key={row.key} className={styles.row}>
            <details className={styles.details}>
              <summary className={styles.summary}>
                <span className={styles.lineOne}>
                  <span className={styles.name}>{row.descriptor}</span>
                  {flatReadable(s) && (
                    <span
                      className={`${styles.pnl} ${pnlClass(s.flat.netPerContract ?? 0)}`}
                    >
                      {cents(s.flat.netPerContract ?? 0)} a contract
                    </span>
                  )}
                </span>
                <span className={styles.lineTwo}>
                  <span>{row.name}</span>
                  {range && <span>{range}</span>}
                </span>
                <span className={styles.lineThree}>{statusLine(s)}</span>
              </summary>
              <div className={styles.body}>
                <RowDetails row={row} rules={rules} />
              </div>
            </details>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Every strategy's paper record on one screen: a three-line row each, led
 * by the per-contract result after fees and its likely range (the figure
 * sizing and busts can't flatter), best first, with the reference
 * baselines in their own group below. The detail (bankroll chart, runs,
 * paid in and left) is one tap away.
 *
 * The figures come from the permanent bankroll ledger (see
 * src/lib/bankrollLedger.ts): a bankroll too small to place the next bet is
 * topped back up and a new numbered run starts, while the lifetime totals
 * run through every run and never reset.
 */
export function Btc15TrackRecord({
  rows,
  rules,
  source,
}: {
  rows: TrackRecordRow[];
  rules: LedgerRules;
  source: TrackRecordSource;
}) {
  const since = rows
    .map((row) => row.summary.sinceMs)
    .filter((ms): ms is number => ms !== null);
  const sinceMs = since.length ? Math.min(...since) : null;
  const through = rows
    .map((row) => row.summary.throughMs)
    .filter((ms): ms is number => ms !== null);
  const throughMs = through.length ? Math.max(...through) : null;
  const pool = formatUsd(rules.startingPool).replace('.00', '');

  return (
    <section className={styles.card} aria-labelledby="btc15-track-record-heading">
      <div className={styles.headingRow}>
        <h2 id="btc15-track-record-heading" className={styles.heading}>
          Paper models
        </h2>
        <span className={styles.badge}>Paper</span>
      </div>
      <p className={styles.note}>
        Each model bets its own {pool} bankroll. When it is too small to place the next
        bet, it is topped back up to {pool} and a new run starts.{' '}
        {source === 'ledger' ? (
          <>
            Totals count every run
            {sinceMs !== null ? ` since ${day(sinceMs)}` : ''} and never reset.
            {/* Stated so a ledger that has stopped updating is visible. */}
            {throughMs !== null && <> Counted through {dateTime.format(throughMs)}.</>}
          </>
        ) : (
          WINDOW_NOTE[source]
        )}
      </p>
      <RowList
        rows={orderTrackRecordRows(rows.filter((row) => !row.baseline))}
        rules={rules}
      />
      {rows.some((row) => row.baseline) && (
        <>
          <h3 className={styles.groupHeading}>Reference baselines</h3>
          <p className={styles.fine}>
            Chance with the same bet sizing and fees: a model needs to beat these to have
            an edge at all.
          </p>
          <RowList
            rows={orderTrackRecordRows(rows.filter((row) => row.baseline))}
            rules={rules}
          />
        </>
      )}
      <p className={styles.fine}>
        Paper only; no real money was risked. Bets are sized by quarter-Kelly, $1 to $20,
        and every figure is after Kalshi&apos;s taker fee. The per-contract figure is one
        contract on every bet, which does not depend on the bankroll.
      </p>
    </section>
  );
}
